import { i18n } from "@/plugin/vue-i18n";
import {
  BUMP_CONDITION_PREFIX,
  CONDITIONAL_OPERATIONS,
  PRODUCT_CONDITION_ANY_VARIANT,
  PRODUCT_CONDITION_PREFIX,
  PRODUCT_CONDITION_SEPARATOR,
} from "@/util/constants";
import {
  dateFormatForDayjs,
  dateFormatForUI,
  standardFields,
} from "@/util/methods";
import dayjs from "dayjs";
import { h, VNode } from "vue";

const { t } = i18n.global;

export interface FieldLike {
  id?: string;
  tag?: string;
  hiddenFieldQueryKey?: string;
  type?: string;
  dataType?: string;
  format?: string;
  separator?: string;
  timeFormat?: string | number;
}

export interface ConditionLike {
  rangeStart?: string | number | null | undefined;
  rangeEnd?: string | number | null | undefined;
  dateFormat?: string;
}

export type ConditionalFieldKind =
  | "date"
  | "time"
  | "date_time"
  | "text"
  | "large_text"
  | "phone"
  | "email"
  | "single_options"
  | "select"
  | "radio"
  | "multiple_options"
  | "checkbox"
  | "monetory"
  | "numerical"
  | "score"
  | "rating"
  | "file_upload"
  | "signature"
  | "terms_and_conditions"
  | string;

const TIME_CONDITION_FORMAT = "HH:mm";
const TIME_PICKER_FORMAT_12 = "h:mm a";
const TIME_DISPLAY_FORMAT_12 = "h:mm A";
const DATE_TIME_CONDITION_FORMAT = "YYYY-MM-DDTHH:mm";
const DATE_TIME_DISPLAY_FORMAT = "YYYY-MM-DD HH:mm";
const TIME_WITH_SECONDS_REGEX = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;
const TIME_WITHOUT_SECONDS_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
const TIME_WITH_MERIDIEM_REGEX = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*(AM|PM)$/i;
const DATE_TIME_INPUT_FORMATS = [
  DATE_TIME_CONDITION_FORMAT,
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DD HH:mm:ss",
];

export interface ParsedConditionDateTimeValue {
  dateValue: number | null;
  timeValue: number | null;
  dateString: string;
  timeString: string;
}

export const resolveConditionalFieldKind = (
  field?: FieldLike
): ConditionalFieldKind | undefined => {
  if (!field?.type) {
    return undefined;
  }

  if (field.type !== "date") {
    return field.type;
  }

  if (field.dataType === "TIME") {
    return "time";
  }

  if (field.dataType === "DATE_TIME") {
    return "date_time";
  }

  return "date";
};

export const isTemporalConditionKind = (fieldKind?: string): boolean =>
  fieldKind === "date" || fieldKind === "time" || fieldKind === "date_time";

export type ProductConditionKind = "product" | "bumpProduct";

export interface ProductConditionRef {
  kind: ProductConditionKind;
  productId: string;
  /**
   * A specific price/variant id, or `null` for the whole product ("any variant").
   * Bump products always reference a concrete price.
   */
  priceId: string | null;
}

/** True when a condition/outcome field key targets a product or bump product. */
export const isProductConditionKey = (key?: string | null): boolean => {
  if (typeof key !== "string") {
    return false;
  }
  return (
    key.startsWith(`${PRODUCT_CONDITION_PREFIX}${PRODUCT_CONDITION_SEPARATOR}`) ||
    key.startsWith(`${BUMP_CONDITION_PREFIX}${PRODUCT_CONDITION_SEPARATOR}`)
  );
};

/**
 * Encode a product/bump target as a synthetic field key.
 * Whole product → `product::<productId>::*`; variant → `product::<productId>::<priceId>`;
 * bump → `bump::<productId>::<priceId>`.
 */
export const buildProductConditionKey = (ref: ProductConditionRef): string => {
  const prefix =
    ref.kind === "bumpProduct"
      ? BUMP_CONDITION_PREFIX
      : PRODUCT_CONDITION_PREFIX;
  const priceSegment = ref.priceId ?? PRODUCT_CONDITION_ANY_VARIANT;
  return [prefix, ref.productId, priceSegment].join(PRODUCT_CONDITION_SEPARATOR);
};

/** Parse a synthetic product/bump key back into a structured ref, or `null` if malformed. */
export const parseProductConditionKey = (
  key?: string | null
): ProductConditionRef | null => {
  if (typeof key !== "string") {
    return null;
  }
  const parts = key.split(PRODUCT_CONDITION_SEPARATOR);
  if (parts.length !== 3) {
    return null;
  }
  const [prefix, productId, priceSegment] = parts;
  if (!productId || !priceSegment) {
    return null;
  }
  if (
    prefix !== PRODUCT_CONDITION_PREFIX &&
    prefix !== BUMP_CONDITION_PREFIX
  ) {
    return null;
  }
  const kind: ProductConditionKind =
    prefix === BUMP_CONDITION_PREFIX ? "bumpProduct" : "product";
  const priceId =
    priceSegment === PRODUCT_CONDITION_ANY_VARIANT ? null : priceSegment;
  return { kind, productId, priceId };
};

const isTwelveHourFormat = (timeFormat?: string | number): boolean =>
  String(timeFormat) === "12";

export const timeFormatForUI = (timeFormat?: string | number): string =>
  isTwelveHourFormat(timeFormat)
    ? TIME_PICKER_FORMAT_12
    : TIME_CONDITION_FORMAT;

const formatTimeForDisplay = (
  value: unknown,
  timeFormat?: string | number
): string | null => {
  const timeValue = toConditionTimeValue(value);
  if (!timeValue) {
    return null;
  }

  const parsed = dayjs(`2000-01-01T${timeValue}`);
  if (!parsed.isValid()) {
    return timeValue;
  }

  return parsed.format(
    isTwelveHourFormat(timeFormat)
      ? TIME_DISPLAY_FORMAT_12
      : TIME_CONDITION_FORMAT
  );
};

const toDayjsValue = (value: unknown): dayjs.Dayjs | null => {
  if (value instanceof Date) {
    const parsedDate = dayjs(value);
    return parsedDate.isValid() ? parsedDate : null;
  }

  if (typeof value === "number") {
    const parsedNumber = dayjs(value);
    return parsedNumber.isValid() ? parsedNumber : null;
  }

  if (typeof value === "string" && value.trim()) {
    const parsedString = dayjs(value);
    return parsedString.isValid() ? parsedString : null;
  }

  return null;
};

export const toConditionTimeValue = (value: unknown): string => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (TIME_WITHOUT_SECONDS_REGEX.test(trimmed)) {
      return trimmed;
    }
    if (TIME_WITH_SECONDS_REGEX.test(trimmed)) {
      return trimmed.slice(0, 5);
    }
    const meridiemMatch = TIME_WITH_MERIDIEM_REGEX.exec(trimmed);
    if (meridiemMatch) {
      const [, hoursPart, minutesPart, meridiemPart] = meridiemMatch;
      let hours = parseInt(hoursPart, 10) % 12;
      if (meridiemPart.toUpperCase() === "PM") {
        hours += 12;
      }
      return `${String(hours).padStart(2, "0")}:${minutesPart}`;
    }
  }

  const parsed = toDayjsValue(value);
  return parsed?.isValid() ? parsed.format(TIME_CONDITION_FORMAT) : "";
};

export const toConditionDateTimeValue = (
  dateValue: unknown,
  timeValue: unknown
): string => {
  const parsedDate = toDayjsValue(dateValue);
  const time = toConditionTimeValue(timeValue);

  if (!parsedDate?.isValid() || !time) {
    return "";
  }

  return `${parsedDate.format("YYYY-MM-DD")}T${time}`;
};

export const parseConditionDateTimeValue = (
  value: unknown
): ParsedConditionDateTimeValue => {
  if (typeof value !== "string" || !value.trim()) {
    return { dateValue: null, timeValue: null, dateString: "", timeString: "" };
  }

  const parsed = DATE_TIME_INPUT_FORMATS.reduce<dayjs.Dayjs | null>(
    (validDate, format) => {
      if (validDate?.isValid()) {
        return validDate;
      }
      const candidate = dayjs(value, format);
      return candidate.isValid() ? candidate : null;
    },
    null
  );

  if (!parsed?.isValid()) {
    return { dateValue: null, timeValue: null, dateString: "", timeString: "" };
  }

  return {
    dateValue: parsed.startOf("day").valueOf(),
    timeValue: parsed.valueOf(),
    dateString: parsed.format("YYYY-MM-DD"),
    timeString: parsed.format(TIME_CONDITION_FORMAT),
  };
};

export const formatTemporalConditionValue = (
  value: string | number | null | undefined,
  fieldType?: string,
  formatStr?: string,
  timeFormat?: string | number,
  storedDateFormat?: string
): string => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  if (fieldType === "time") {
    return formatTimeForDisplay(value, timeFormat) || String(value);
  }

  if (fieldType === "date_time") {
    const parsedDateTime = parseConditionDateTimeValue(value);
    if (parsedDateTime.dateValue && parsedDateTime.timeString) {
      const displayFormat = formatStr || "YYYY-MM-DD";
      const displayTime =
        formatTimeForDisplay(parsedDateTime.timeString, timeFormat) ||
        parsedDateTime.timeString;
      return (
        dayjs(parsedDateTime.dateValue).format(displayFormat) +
        ` ${displayTime}`
      );
    }
    const fallback = dayjs(value);
    return fallback.isValid()
      ? fallback.format(DATE_TIME_DISPLAY_FORMAT)
      : String(value);
  }

  if (fieldType === "date") {
    const displayFormat = formatStr || "YYYY-MM-DD";
    const parseFormat = storedDateFormat || displayFormat;
    const parsed = dayjs(value, parseFormat);
    return parsed.isValid() ? parsed.format(displayFormat) : String(value);
  }

  return String(value);
};

const formatTemporalBetweenValue = (
  value: string | number | null | undefined,
  fieldType?: string,
  formatStr?: string,
  timeFormat?: string | number,
  storedDateFormat?: string
): string | null => {
  const formatted = formatTemporalConditionValue(
    value,
    fieldType,
    formatStr,
    timeFormat,
    storedDateFormat
  );
  return formatted || null;
};

/**
 * Resolve an icon VNode for a given field. Matches by tag first, then by type when id exists,
 * and finally by hiddenFieldQueryKey when there is no id. Includes a special case for phone.
 */
export const getOptionIconVNode = (field?: FieldLike): VNode | null => {
  try {
    for (const groupKey of Object.keys(standardFields as any)) {
      const group = (standardFields as any)[groupKey]?.group;
      if (!Array.isArray(group)) {
        continue;
      }

      const found =
        (field?.tag
          ? group.find((item: any) => item?.tag === field.tag)
          : undefined) ||
        (field?.id && field?.type
          ? group.find((item: any) => item?.type === field.type) ||
            (field?.type === "phone" || field?.dataType === "PHONE"
              ? group.find((item: any) => item?.tag === "phone")
              : undefined)
          : undefined) ||
        (!field?.id && field?.hiddenFieldQueryKey
          ? group.find(
              (item: any) =>
                item?.hiddenFieldQueryKey === field.hiddenFieldQueryKey
            )
          : undefined);

      if (found?.icon) {
        const isRating =
          field?.type === "rating" ||
          field?.tag === "rating" ||
          field?.dataType === "RATING";
        let produced: any =
          typeof found.icon === "function"
            ? (found.icon as any)()
            : (found.icon as any);
        if (!produced) {
          return null;
        }

        // Some icons (e.g., rating) may return an array/fragment – choose the primary icon
        if (Array.isArray(produced)) {
          // Prefer the last non-null node (commonly the actual icon component)
          produced = produced.filter(Boolean).pop();
          if (!produced) {
            return null;
          }
        }

        // Normalize <img> nodes with consistent sizing
        if ((produced as any).type === "img") {
          const src = (produced as any)?.props?.src;
          return h("img", {
            src,
            style: {
              width: isRating ? "32px" : "24px",
              height: isRating ? "32px" : "24px",
              objectFit: "contain",
            },
          });
        }

        // Preserve original props while enforcing size
        const vnodeType = (produced as any).type || produced;
        const originalProps = ((produced as any).props || {}) as Record<
          string,
          any
        >;
        const mergedStyle = {
          ...(originalProps.style || {}),
          width: isRating ? "32px" : "24px",
          height: isRating ? "32px" : "24px",
        } as Record<string, any>;
        return h(vnodeType as any, { ...originalProps, style: mergedStyle });
      }
    }
  } catch {
    return null;
  }
  return null;
};

/**
 * Build shortcuts for date range picker.
 */
export const dateRangeShortcuts = (): Record<string, [number, number]> => {
  const endOfToday = dayjs().endOf("day").valueOf();
  return {
    [t("common.today")]: [dayjs().startOf("day").valueOf(), endOfToday],
    [t("common.thisWeek")]: [
      dayjs().startOf("week").valueOf(),
      dayjs().endOf("week").valueOf(),
    ],
    [t("common.last7Days")]: [
      dayjs().subtract(6, "day").startOf("day").valueOf(),
      endOfToday,
    ],
    [t("common.thisMonth")]: [
      dayjs().startOf("month").valueOf(),
      dayjs().endOf("month").valueOf(),
    ],
    [t("common.last30Days")]: [
      dayjs().subtract(29, "day").startOf("day").valueOf(),
      endOfToday,
    ],
  } as Record<string, [number, number]>;
};

/**
 * Resolve the UI date format for a selected field.
 */
// Find first matching element by hiddenFieldQueryKey across slides
const findElementByHiddenKey = (
  slides: any[],
  selectedField: string
): any | null => {
  for (const slide of slides) {
    if (slide?.slideData && Array.isArray(slide.slideData)) {
      const found = slide.slideData.find(
        (item: any) => item?.hiddenFieldQueryKey === selectedField
      );
      if (found) {
        return found;
      }
    }
  }
  return null;
};

export const dateTypeFieldFormat = (
  store: any,
  selectedField?: string
): string => {
  if (!selectedField) {
    return dateFormatForUI();
  }
  const dateElement = store.formId
    ? store.elements.find(
        (item: any) => item?.hiddenFieldQueryKey === selectedField
      )
    : findElementByHiddenKey(store.slides, selectedField);
  return dateFormatForUI(dateElement?.format, dateElement?.separator);
};

/**
 * Resolve the Dayjs date format for a selected field.
 */
export const dateTypeFieldFormatDayjs = (
  store: any,
  selectedField?: string
): string => {
  if (!selectedField) {
    return dateFormatForDayjs();
  }
  const dateElement = store.formId
    ? store.elements.find(
        (item: any) => item?.hiddenFieldQueryKey === selectedField
      )
    : findElementByHiddenKey(store.slides, selectedField);
  return dateFormatForDayjs(dateElement?.format, dateElement?.separator);
};
/**
 * Format to use when setting the datepicker value (parsing stored date strings).
 * Prefers the format stored on the condition so rendering stays correct if the
 * field's format in store is changed later.
 */
export const dateTypeFieldFormatDayjsForCondition = (
  store: any,
  condition: { dateFormat?: string; selectedField?: string | null }
): string => {
  if (condition?.dateFormat) {
    return condition.dateFormat;
  }
  return dateTypeFieldFormatDayjs(store, condition?.selectedField ?? undefined);
};

/**
 * Convert a stored date string to the numeric value UIDatepicker expects.
 * Uses storedFormat when provided (format from condition at save time) so the
 * datepicker value is correct even if the field's format in store changed.
 */
export const getDateTypeFieldValue = (
  store: any,
  selectedField: string | undefined,
  inputValue: any,
  storedFormat?: string
): number => {
  const fallbackFormat = dateTypeFieldFormatDayjs(store, selectedField);
  const format = storedFormat || fallbackFormat;
  const date = dayjs(inputValue, format).isValid()
    ? dayjs(inputValue, format).valueOf()
    : new Date().valueOf();
  return date;
};

/**
 * Determine whether to hide the value input for operations that don't need it.
 */
export const shouldHideInput = (selectedOperation?: string): boolean => {
  return (
    [
      CONDITIONAL_OPERATIONS.IS_FILLED,
      CONDITIONAL_OPERATIONS.IS_EMPTY,
      CONDITIONAL_OPERATIONS.IS_CHECKED,
      CONDITIONAL_OPERATIONS.IS_NOT_CHECKED,
      CONDITIONAL_OPERATIONS.IS_SELECTED,
      CONDITIONAL_OPERATIONS.IS_NOT_SELECTED,
    ] as string[]
  ).includes(selectedOperation as any);
};

/**
 * Render label for Include Start checkbox.
 */
export const includeStartText = (
  condition: ConditionLike,
  fieldType?: string,
  formatStr?: string,
  timeFormat?: string | number
): string => {
  if (isTemporalConditionKind(fieldType)) {
    const temporalValue = formatTemporalBetweenValue(
      condition?.rangeStart,
      fieldType,
      formatStr,
      timeFormat,
      condition?.dateFormat
    );
    if (temporalValue) {
      return t("form.conditonalLogic.includeValue", { value: temporalValue });
    }
  }

  if (["numerical", "monetory", "score", "rating"].includes(fieldType || "")) {
    if (!condition?.rangeStart) {
      return t("form.conditonalLogic.includeStart");
    }
    const val =
      fieldType === "monetory" &&
      !isNaN(parseFloat(String(condition.rangeStart)))
        ? parseFloat(String(condition.rangeStart)).toFixed(2)
        : String(condition.rangeStart);
    return t("form.conditonalLogic.includeValue", { value: val });
  }
  return t("form.conditonalLogic.includeStart");
};

/**
 * Render label for Include End checkbox.
 */
export const includeEndText = (
  condition: ConditionLike,
  fieldType?: string,
  formatStr?: string,
  timeFormat?: string | number
): string => {
  if (isTemporalConditionKind(fieldType)) {
    const temporalValue = formatTemporalBetweenValue(
      condition?.rangeEnd,
      fieldType,
      formatStr,
      timeFormat,
      condition?.dateFormat
    );
    if (temporalValue) {
      return t("form.conditonalLogic.includeValue", { value: temporalValue });
    }
  }
  if (["numerical", "monetory", "score", "rating"].includes(fieldType || "")) {
    if (!condition?.rangeEnd) {
      return t("form.conditonalLogic.includeEnd");
    }
    const val =
      fieldType === "monetory" && !isNaN(parseFloat(String(condition.rangeEnd)))
        ? parseFloat(String(condition.rangeEnd)).toFixed(2)
        : String(condition.rangeEnd);
    return t("form.conditonalLogic.includeValue", { value: val });
  }
  return t("form.conditonalLogic.includeEnd");
};
